import React, { useCallback, useContext, useState } from "react";
import { getAddressDetail, suggestAddress } from "../../../../api/cybwell";
import { Field } from "formik";
import get from "lodash.get";
import { useMessageSource } from "react-message-source";
import { ErrorContext } from "../../../../route/error/provider";
import GridContainerItem from "../../../../ui/layout/page/container-item";
import CityFieldContainer from "../../../../ui/layout/page/forms-container/form-item-container/city-field-container";
import InputContainer from "../../../../ui/layout/page/forms-container/form-item-container/input-container";
import ZipCodeFieldContainer from "../../../../ui/layout/page/forms-container/form-item-container/zipcode-field-container";
import { formatFullStreet } from "../../../../util/format";
import AddressAutocomplete from "../autocomplete/address-autocomplete";
import TextInputField from "../formik/text-input";
import { PostAddress } from "../../../../api/commercial/generated-types";
import {
  CybwellAddress,
  CybwellSuggestedAddress,
} from "../autocomplete/address-autocomplete/types";
import { FieldProps } from "formik/dist/Field";

export default function AddressSelect({
  fullStreet,
  address,
  dataPath,
  isBusinessAddress,
}: {
  fullStreet: string;
  address: PostAddress;
  dataPath: string;
  isBusinessAddress?: boolean;
}): React.JSX.Element {
  const { getMessage } = useMessageSource();

  const { setError } = useContext(ErrorContext);

  const [isLabelShrunkZipCode, setIsLabelShrunkZipCode] = useState(false);
  const [isLabelShrunkCity, setIsLabelShrunkCity] = useState(false);

  /**
   * Triggers an address search.
   * @param searchString the search string.
   */
  const doSuggestAddress = useCallback(
    async (searchString: string) => {
      const trimmedString = searchString?.trim();
      try {
        return trimmedString && trimmedString.length > 0
          ? await suggestAddress(trimmedString)
          : [];
      } catch (e) {
        setError(e);
        return [];
      }
    },
    [setError],
  );

  /**
   * Triggers an address fetch.
   * @param data the search string.
   */
  const doGetAddressDetail = useCallback(
    async (data: CybwellSuggestedAddress): Promise<CybwellAddress> => {
      if (data) {
        try {
          return await getAddressDetail(data.legacyID);
        } catch (e) {
          setError(e);
        }
      } else {
        return undefined;
      }
    },
    [setError],
  );

  return (
    <>
      <InputContainer>
        <Field name={`${dataPath}fullStreet`} key={`${dataPath}fullStreet`}>
          {({ form, field }: FieldProps) => {
            const error = get(form, `errors.${field.name}`);
            const touched = get(form, `touched.${field.name}`);

            return (
              <AddressAutocomplete
                label={
                  isBusinessAddress
                    ? getMessage("Company.address")
                    : getMessage("Person.address")
                }
                id={field.name}
                value={field.value}
                name={field.name}
                selected={{ legacyID: null, label: fullStreet }}
                error={!!error && !!touched}
                helperText={(touched && error) as string}
                onBlur={field.onBlur}
                onSearchInputChange={(event) => {
                  const searchTerm = event.target.value;
                  form.setFieldValue(`${dataPath}address.streetNr`, "");
                  form.setFieldValue(`${dataPath}address.street`, searchTerm);
                  form.setFieldValue(`${dataPath}fullStreet`, searchTerm);
                }}
                doSearch={doSuggestAddress}
                onSelect={async (data) => {
                  setIsLabelShrunkCity(true);
                  setIsLabelShrunkZipCode(true);

                  const selectedAddress = await doGetAddressDetail(data);

                  form.setFieldValue(
                    `${dataPath}fullStreet`,
                    selectedAddress && formatFullStreet(selectedAddress),
                  );
                  form.setFieldValue(
                    `${dataPath}address.street`,
                    selectedAddress && selectedAddress.street,
                  );
                  form.setFieldValue(
                    `${dataPath}address.streetNr`,
                    selectedAddress && selectedAddress.streetNr,
                  );
                  form.setFieldValue(
                    `${dataPath}address.city`,
                    selectedAddress && selectedAddress.city,
                  );
                  form.setFieldValue(
                    `${dataPath}address.zipCode`,
                    selectedAddress && selectedAddress.zipCode,
                  );
                }}
                itemToSuggestion={(item) => (item ? item.label : "")}
              />
            );
          }}
        </Field>
      </InputContainer>
      <GridContainerItem>
        <ZipCodeFieldContainer>
          <TextInputField
            onChange={(event) => {
              const value = event.target.value;
              if (!value && isLabelShrunkZipCode) {
                setIsLabelShrunkZipCode(false);
              }
            }}
            type="tel"
            shrink={isLabelShrunkZipCode}
            key={`${dataPath}address.zipCode`}
            id={`${dataPath}address.zipCode`}
            name={`${dataPath}address.zipCode`}
            label={getMessage("Person.zip")}
            value={address ? address.zipCode : ""}
          />
        </ZipCodeFieldContainer>
        <CityFieldContainer>
          <TextInputField
            onChange={(event) => {
              const value = event.target.value;

              if (!value && isLabelShrunkCity) {
                setIsLabelShrunkCity(false);
              }
            }}
            shrink={isLabelShrunkCity}
            key={`${dataPath}address.city`}
            id={`${dataPath}address.city`}
            name={`${dataPath}address.city`}
            label={getMessage("Person.city")}
            value={address ? address.city : ""}
          />
        </CityFieldContainer>
      </GridContainerItem>
    </>
  );
}
